import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

export type MMECPalette = 'hg' | 'sheet' | 'neon' | 'cb';
export type MMECMode = 'advanced' | 'simple';

interface MMECThemeContextValue {
  palette: MMECPalette;
  setPalette: (p: MMECPalette) => void;
  mode: MMECMode;
  setMode: (m: MMECMode) => void;
}

const MMECThemeContext = createContext<MMECThemeContextValue | undefined>(undefined);

const LS_PALETTE = 'mmec_palette';
const LS_MODE = 'mmec_mode';

function readPalette(): MMECPalette {
  try {
    const stored = localStorage.getItem(LS_PALETTE);
    if (stored === 'hg' || stored === 'sheet' || stored === 'neon' || stored === 'cb') {
      return stored;
    }
  } catch {}
  return 'hg';
}

function readMode(): MMECMode {
  try {
    const url = new URLSearchParams(window.location.search);
    const exp = url.get('experience');
    if (exp === 'simple') return 'simple';
    if (exp === 'pro' || exp === 'advanced') return 'advanced';
    const stored = localStorage.getItem(LS_MODE);
    if (stored === 'simple' || stored === 'advanced') return stored;
  } catch {}
  return 'advanced';
}

function applyPalette(palette: MMECPalette) {
  const root = document.documentElement;
  if (palette === 'hg') {
    root.removeAttribute('data-palette');
  } else {
    root.setAttribute('data-palette', palette);
  }
}

export const MMECThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [palette, setPaletteState] = useState<MMECPalette>(readPalette);
  const [mode, setModeState] = useState<MMECMode>(readMode);

  // Keep `data-palette` in sync before descendants render so ThemeProvider can
  // read resolved MMEC tokens in the same commit as palette changes.
  if (typeof document !== 'undefined') {
    applyPalette(palette);
  }

  useEffect(() => {
    try {
      localStorage.setItem(LS_PALETTE, palette);
    } catch {}
  }, [palette]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_MODE, mode);
    } catch {}
  }, [mode]);

  const setPalette = useCallback((p: MMECPalette) => setPaletteState(p), []);
  const setMode = useCallback((m: MMECMode) => setModeState(m), []);

  const value = useMemo<MMECThemeContextValue>(
    () => ({ palette, setPalette, mode, setMode }),
    [palette, setPalette, mode, setMode]
  );

  return <MMECThemeContext.Provider value={value}>{children}</MMECThemeContext.Provider>;
};

export function useMMECTheme(): MMECThemeContextValue {
  const ctx = useContext(MMECThemeContext);
  if (!ctx) {
    throw new Error('useMMECTheme must be used within MMECThemeProvider');
  }
  return ctx;
}
